import { z } from 'zod';
import { PageQuerySchema, UuidSchema } from './common';
import { ProjectStatusSchema } from './enums';

/** §6.1, §13 ТЗ. Объекты обслуживания и их менеджеры. */

export const ProjectManagerRefSchema = z.object({
  id: UuidSchema,
  fullName: z.string(),
});
export type ProjectManagerRef = z.infer<typeof ProjectManagerRefSchema>;

export const ProjectSummarySchema = z.object({
  id: UuidSchema,
  name: z.string(),
  address: z.string().nullable(),
  timezone: z.string(),
  status: ProjectStatusSchema,
  groupTag: z.string().nullable(),
  /** §6.1: null — объект автономный, не привязан к проекту Bitrix24 */
  bitrixGroupId: z.number().int().nullable(),
  manager: ProjectManagerRefSchema.nullable(),
  /** Дополнение №1, А13: наблюдатели задач Bitrix24 по объекту (менеджер КО) */
  observers: z.array(ProjectManagerRefSchema),
  /** активные связи «администратор ↔ объект» */
  activeAdmins: z.number().int().min(0),
  createdAt: z.string().datetime(),
});
export type ProjectSummary = z.infer<typeof ProjectSummarySchema>;

export const ProjectDetailSchema = ProjectSummarySchema.extend({
  description: z.string().nullable(),
  /** 'OWNER_ID' | 'EXPLICIT_FIELD' (§6.1) — свойство портала, не объекта */
  managerSource: z.string(),
  syncedAt: z.string().datetime().nullable(),
  archivedAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime(),
});
export type ProjectDetail = z.infer<typeof ProjectDetailSchema>;

/**
 * Создание объекта в автономном режиме (сценарий 2).
 * Привязка к Bitrix24 — отдельная операция синхронизации, не это поле:
 * `bitrixGroupId` в запросе отсутствует намеренно (§6.1 — неизменяем).
 */
export const CreateProjectRequestSchema = z.object({
  name: z.string().trim().min(2).max(200),
  address: z.string().trim().min(1).max(300).optional(),
  description: z.string().trim().min(1).max(2000).optional(),
  /**
   * IANA-зона; корректность проверяет сервер (§9.2). Не передана — сервер
   * берёт зону по умолчанию из конфигурации (`DEFAULT_TIMEZONE`)
   */
  timezone: z.string().trim().min(1).max(64).optional(),
  groupTag: z.string().trim().min(1).max(100).optional(),
  /**
   * §11.2: исполнителем задачи становится менеджер объекта, поэтому объект без
   * ответственного бесполезен для интеграции. Назначение здесь — тот же шаг
   * §13 «назначить менеджера», сделанный сразу, а не следующей операцией.
   */
  managerId: UuidSchema.optional(),
});
export type CreateProjectRequest = z.infer<typeof CreateProjectRequestSchema>;

/** `null` очищает поле, отсутствие ключа оставляет как есть. Статус здесь не меняется. */
export const UpdateProjectRequestSchema = z
  .object({
    name: z.string().trim().min(2).max(200).optional(),
    address: z.string().trim().min(1).max(300).nullable().optional(),
    description: z.string().trim().min(1).max(2000).nullable().optional(),
    timezone: z.string().trim().min(1).max(64).optional(),
    groupTag: z.string().trim().min(1).max(100).nullable().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'Не передано ни одного поля для изменения',
  });
export type UpdateProjectRequest = z.infer<typeof UpdateProjectRequestSchema>;

/** E6: один активный менеджер; `null` снимает назначение. */
export const AssignManagerRequestSchema = z.object({
  managerId: UuidSchema.nullable(),
});
export type AssignManagerRequest = z.infer<typeof AssignManagerRequestSchema>;

/**
 * Дополнение №1, А13. Наблюдатели задач Bitrix24 по объекту — полный новый
 * список; пустой список снимает всех. Исполнитель от этого не меняется.
 */
export const SetObserversRequestSchema = z.object({
  observerIds: z
    .array(UuidSchema)
    .max(10)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'Сотрудники повторяются' }),
});
export type SetObserversRequest = z.infer<typeof SetObserversRequestSchema>;

export const ListProjectsQuerySchema = PageQuerySchema.extend({
  status: ProjectStatusSchema.optional(),
  managerId: UuidSchema.optional(),
  /** по названию и адресу, без учёта регистра */
  search: z.string().trim().min(1).max(100).optional(),
});
export type ListProjectsQuery = z.infer<typeof ListProjectsQuerySchema>;

// ─────────────────── Синхронизация с Bitrix24 (§6.1, T08) ───────────────────

/** Проект портала в диалоге привязки. */
export const B24ProjectOptionSchema = z.object({
  bitrixGroupId: z.number().int(),
  name: z.string(),
  active: z.boolean(),
  /** уже привязанный к этой группе объект — повторная привязка невозможна */
  linkedProjectId: UuidSchema.nullable(),
  /** §6.1: владелец проекта портала — кандидат в менеджеры объекта */
  ownerId: z.number().int().nullable(),
  ownerFullName: z.string().nullable(),
  /** сотрудник, сопоставленный с этим владельцем; null — его нужно завести */
  ownerEmployeeId: UuidSchema.nullable(),
});
export type B24ProjectOption = z.infer<typeof B24ProjectOptionSchema>;

/** Привязка существующего автономного объекта. Отвязки не существует (§6.1). */
export const LinkProjectRequestSchema = z.object({
  bitrixGroupId: z.number().int().positive(),
});
export type LinkProjectRequest = z.infer<typeof LinkProjectRequestSchema>;

/** Создание объекта из проекта портала (сценарий 1). Название берётся из портала. */
export const CreateFromBitrixRequestSchema = z.object({
  bitrixGroupId: z.number().int().positive(),
  /** не передана — зона по умолчанию из конфигурации сервера */
  timezone: z.string().trim().min(1).max(64).optional(),
  groupTag: z.string().trim().min(1).max(100).optional(),
  /**
   * Сценарий 1 требует объект «с корректным `bitrix_group_id` **и менеджером**».
   * При `B24_MANAGER_SOURCE=OWNER_ID` менеджер выводится из владельца проекта,
   * и поле можно не передавать; при `EXPLICIT_FIELD` вывести его неоткуда.
   */
  managerId: UuidSchema.optional(),
});
export type CreateFromBitrixRequest = z.infer<typeof CreateFromBitrixRequestSchema>;

/** Итог прохода синхронизации — по команде и по расписанию один и тот же. */
export const ProjectSyncReportSchema = z.object({
  outcome: z.enum(['OK', 'B24_UNAVAILABLE']),
  checked: z.number().int().min(0),
  renamed: z.number().int().min(0),
  reactivated: z.number().int().min(0),
  managerChanged: z.number().int().min(0),
  /** проекты, пропавшие из портала: ждут подтверждения перевода в INACTIVE (§6.1) */
  needsDeactivation: z.array(z.object({ projectId: UuidSchema, name: z.string() })),
  /** OWNER_ID без сопоставленного сотрудника — менеджер не назначен */
  unmatchedOwners: z.array(z.object({ projectId: UuidSchema, ownerId: z.number().int() })),
});
export type ProjectSyncReport = z.infer<typeof ProjectSyncReportSchema>;
